import { useMemo, useCallback } from 'react';
import useTealiumTrigger, { useCustomTealiumTrigger } from '../../../hooks/useTealiumTrigger';

/**
 * Tracks invalid join form submissions for analytics
 *
 * @param {string} errorKey - The error key to track
 * @param {Object} analyticsTrackingFunctions - Object containing tracking functions
 */
export const trackInvalidJoin = (errorKey, analyticsTrackingFunctions) => {
  const {
    trackInvalidPassword, trackInvalidCreateAccount, trackInvalidSignIn, trackInvalidPhoneNumber,
  } = analyticsTrackingFunctions;

  const errorKeyToFunction = {
    ACT_PasswordCharacterLimits2: trackInvalidPassword,
    _ERR_MISSING_PARMS: trackInvalidCreateAccount,
    INVALID_PHONE_NUMBER: trackInvalidPhoneNumber,
    default: trackInvalidSignIn,
  };

  const trackActionFunction = errorKeyToFunction[errorKey] || errorKeyToFunction.default;
  trackActionFunction();
};

/**
 * Custom hook for managing all analytics tracking in the join form workflow.
 *
 * This hook consolidates all Tealium analytics logic including success tracking,
 * error tracking, and data preparation. It provides a centralized interface for
 * all analytics-related functionality in the join form.
 *
 * @param {Object} params - Hook configuration parameters
 * @param {string} params.userCountry - User's country code for analytics
 * @param {string} params.signifydId - Device ID for analytics
 * @param {boolean} params.emailOptIn - User's email opt-in preference
 * @param {boolean} params.keepMeLoggedInFlag - Keep me logged in preference
 *
 * @returns {Object} Hook interface
 * @returns {Object} returns.invalidTrackingFunctions - Functions for tracking validation failures
 * @returns {Function} returns.trackInvalidJoin - Function to track invalid submissions
 * @returns {Function} returns.trackSuccessfulSignIn - Function to track successful sign-in
 * @returns {Function} returns.trackAccountCreationSuccess - Combined function to track both success events
 */
export const useJoinFormAnalytics = ({
  userCountry,
  signifydId,
  emailOptIn,
  keepMeLoggedInFlag,
}) => {
  // Invalid tracking functions setup
  const trackInvalidPassword = useTealiumTrigger('universal', 'INVALID_PASSWORD');
  const trackInvalidSignIn = useTealiumTrigger('universal', 'SIGN_IN_FAIL');
  const trackInvalidCreateAccount = useTealiumTrigger('universal', 'INVALID_CREATE_ACCOUNT');
  const trackInvalidPhoneNumber = useTealiumTrigger('universal', 'INVALID_PHONE_NUMBER');

  const invalidTrackingFunctions = useMemo(() => ({
    trackInvalidPassword,
    trackInvalidSignIn,
    trackInvalidCreateAccount,
    trackInvalidPhoneNumber,
  }), [trackInvalidPassword, trackInvalidSignIn, trackInvalidCreateAccount, trackInvalidPhoneNumber]);

  // Success analytics data objects
  const loginData = useMemo(() => ({
    eventName: 'analytics.login_success',
    eventData: {
      event_type: 'login_success',
      site_country: userCountry || '',
      user_device_id: signifydId || '',
      user_wcs_id: '',
      user_state: 'authenticated' || '',
      keep_me_logged_in_flag: keepMeLoggedInFlag,
    },
  }), [userCountry, signifydId, keepMeLoggedInFlag]);

  const baseCreateAccountData = useMemo(() => ({
    eventName: 'analytics.account_create_success',
    eventData: {
      event_type: 'account_create_success',
      site_country: userCountry || '',
      user_device_id: signifydId || '',
      user_wcs_id: '',
      user_state: 'authenticated' || '',
      email_opt_in: emailOptIn,
      keep_me_logged_in: keepMeLoggedInFlag,
    },
  }), [userCountry, signifydId, emailOptIn, keepMeLoggedInFlag]);

  const trackSuccessfulSignIn = useCustomTealiumTrigger(loginData);

  const trackAccountCreationSuccess = useCallback((userId, passwordStrength) => {
    // Update login data with user ID and track sign in
    loginData.eventData.user_wcs_id = userId;
    trackSuccessfulSignIn();

    // Create and dispatch account creation event directly
    const createAccountEvent = {
      ...baseCreateAccountData,
      eventData: {
        ...baseCreateAccountData.eventData,
        password_strength: passwordStrength,
        user_wcs_id: userId,
      },
    };

    // Dispatch the event directly to Tealium
    if (window.utag && window.utag.link) {
      window.utag.link(createAccountEvent.eventData);
    }
  }, [loginData, trackSuccessfulSignIn, baseCreateAccountData]);

  return {
    // Invalid tracking
    invalidTrackingFunctions,
    trackInvalidJoin: (errorKey) => trackInvalidJoin(errorKey, invalidTrackingFunctions),

    // Success tracking
    trackSuccessfulSignIn,
    trackAccountCreationSuccess,
  };
};
